import { keyframes, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent } from 'react';

import { Card } from '~/ui/core/card/Card';

import { jobArchitectureCardDefaultHeight } from './card-layout';

const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

const SkeletonBase = styled.div(({ theme }) => ({
    animation: `${shimmer} 2s ease-in-out infinite`,
    background:
        'linear-gradient(90deg, var(--color-skeleton-base) 0%, var(--color-skeleton-highlight) 50%, var(--color-skeleton-base) 100%)',
    backgroundSize: '1000px 100%',
    borderRadius: theme.borderRadius.m,
}));

const SkeletonTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '60%',
}));

const SkeletonContent = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '20%',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.small,
    width: '100%',
}));

type SkeletonCardsProps = {
    readonly count?: number;
};

export const SkeletonCard: FunctionComponent = () => {
    return (
        // Decorative loading placeholder with no focusable content: keep it out of the accessibility
        // tree so it is not announced, and is not counted among the surrounding listbox's children.
        <StyledCard
            aria-hidden={true}
            data-testid="loading-skeleton"
            style={{ height: jobArchitectureCardDefaultHeight }}
        >
            <div className="flex min-h-4xl items-center">
                <SkeletonTitle />
            </div>
            <div className="flex min-h-[26px] items-center">
                <SkeletonContent />
            </div>
        </StyledCard>
    );
};

export const SkeletonCards: FunctionComponent<SkeletonCardsProps> = ({ count = 1 }) => {
    const theme = useTheme();

    return (
        <AnimatePresence mode="popLayout">
            <motion.div
                key="skeleton-cards-container"
                animate="visible"
                exit="hidden"
                initial="hidden"
                variants={{
                    exit: {
                        transition: {
                            staggerChildren: 0.05,
                            staggerDirection: -1,
                        },
                    },
                    hidden: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.m,
                    },
                    visible: {
                        transition: {
                            delayChildren: 0,
                            staggerChildren: 0.1,
                        },
                    },
                }}
            >
                {Array.from({ length: count }, (_, index) => (
                    <motion.div
                        // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                        key={index}
                        variants={{
                            hidden: { opacity: 0, scale: 0.95, x: -50 },
                            visible: { opacity: 1, scale: 1, x: 0 },
                        }}
                    >
                        <SkeletonCard />
                    </motion.div>
                ))}
            </motion.div>
        </AnimatePresence>
    );
};
